/**
 * MED-699: Participant-facing check-in line item for the Group Coaching session timeline.
 *
 * Renders Intro and Midpoint check-in placeholders as distinct rows inserted into the
 * numbered session list. Visually distinct badge (chat-bubble icon) so rows don't read
 * as sessions. Right-aligned status pill + contextual Schedule button when unscheduled.
 */

import { Group, Box, Text, Badge, Button, ThemeIcon, Tooltip } from '@mantine/core';
import { MessageSquare, Calendar, Check } from 'react-feather';
import type { ParticipantTypedCheckin } from '../../../services/api/participantPortal.js';
import { CheckinActionsMenu } from './CheckinActionsMenu.js';
import { AttendancePendingBadge } from './AttendancePendingBadge.js';
import { isPastAwaitingAttendance } from '../../cohorts/components/sessions/sessionsHelpers';
import { canChangeBooking } from '../bookingActions.js';
import { AWAITING_COACH_TOOLTIP_COPY } from '../awaitingCoachSchedule.js';
import { getZoneCode } from '../../../utils/formatters';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  // MED-1389: name the zone ("9:00 AM ET"), as the coach view does.
  return `${time} ${getZoneCode(null, d)}`;
}

/** MED-1350: a check-in is 20 minutes when its row has no length. */
const CHECKIN_DEFAULT_MINUTES = 20;

export const CHECKIN_TYPE_LABELS: Record<'intro' | 'midpoint_checkin', string> = {
  intro: 'Intro check-in',
  midpoint_checkin: 'Midpoint check-in',
};

interface CheckinLineItemProps {
  huddleType: 'intro' | 'midpoint_checkin';
  /** May be null when MED-697 auto-creation did not run for this cohort — still render with Schedule disabled + fallback. */
  checkin: ParticipantTypedCheckin | null;
  /** When false, the Schedule button is hidden and a fallback message is shown (AC18). */
  canSchedule: boolean;
  /** Coach email for the "Contact your coach" fallback (AC18). */
  coachEmail: string | null;
  /** MED-845 (Kelley QA follow-up, AC11): Gates the hover tooltip on the
   * "Needs Scheduling" pill. When false, the tooltip explains the disabled state.
   * When true (happy path), the tooltip is suppressed and the pill behaves exactly as before. */
  coachCalendlyConnected: boolean;
  onSchedule: () => void;
  /** MED-840: Open the reschedule modal scoped to this check-in. Only invoked when isScheduled. */
  onReschedule?: () => void;
  /** MED-840: Open the cancel confirmation scoped to this check-in. Only invoked when isScheduled. */
  onCancel?: () => void;
}

export function CheckinLineItem({
  huddleType,
  checkin,
  canSchedule,
  coachEmail,
  coachCalendlyConnected,
  onSchedule,
  onReschedule,
  onCancel,
}: CheckinLineItemProps) {
  const title = CHECKIN_TYPE_LABELS[huddleType];
  const status = checkin?.status ?? 'unscheduled';
  const isCompleted = status === 'completed';
  const isScheduled = status === 'scheduled' && !!checkin?.scheduled_start;
  // MED-840 AC13: a typed check-in cancelled by the participant transitions to 'cancelled'.
  // Row must reflect the canceled state and not offer re-scheduling (the typed slot is consumed).
  const isCancelled = status === 'cancelled';

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      px="md"
      py="sm"
      style={{ backgroundColor: '#FAF9F6' }}
    >
      <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        {/* Visually distinct badge — NOT a numbered circle */}
        <ThemeIcon
          size={36}
          radius="xl"
          variant="light"
          color={isCompleted ? 'medleyGreen' : 'gray'}
          style={{ flexShrink: 0 }}
        >
          {isCompleted ? <Check size={16} /> : <MessageSquare size={16} />}
        </ThemeIcon>
        <Box style={{ minWidth: 0 }}>
          {/* MED-1350: a check-in is a 20 minute 1:1. Name its length and its coach. */}
          <Group gap={6} wrap="wrap">
            <Text size="sm" fw={600}>
              {title}
            </Text>
            <Badge size="xs" variant="light" color="gray">
              {checkin?.duration_minutes ?? CHECKIN_DEFAULT_MINUTES} min
            </Badge>
          </Group>
          {checkin?.coach_name && (
            <Text size="xs" c="dimmed">
              With {checkin.coach_name}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {isScheduled || isCompleted
              ? `${formatDate(checkin!.scheduled_start!)} · ${formatTime(checkin!.scheduled_start!)}`
              : isCancelled
                ? coachEmail
                  ? `Canceled — contact ${coachEmail} to schedule a replacement`
                  : 'Canceled — contact your coach to schedule a replacement'
                : canSchedule
                  ? 'Required — book when ready'
                  : coachEmail
                    ? `Contact ${coachEmail} to schedule`
                    : 'Contact your coach to schedule'}
          </Text>
        </Box>
      </Group>

      <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
        {isCompleted && (
          <Badge
            size="xs"
            color="medleyGreen"
            variant="light"
            style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Completed
          </Badge>
        )}
        {isScheduled && (
          <>
            {/* MED-1360: a past check-in the coach has not marked is no longer scheduled. */}
            {isPastAwaitingAttendance(checkin!) ? (
              <AttendancePendingBadge />
            ) : (
              <Badge
                size="xs"
                color="medleyGreen"
                variant="light"
                style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
              >
                Scheduled
              </Badge>
            )}
            {/* MED-840 AC1/AC2/AC4/AC5/AC6: 3-dot menu visible only on scheduled rows.
                MED-1360: and only before the check-in ends. */}
            {onReschedule && onCancel && canChangeBooking(checkin!) && (
              <CheckinActionsMenu onReschedule={onReschedule} onCancel={onCancel} />
            )}
          </>
        )}
        {isCancelled && (
          <Badge
            size="xs"
            color="dangerRed"
            variant="light"
            style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Canceled
          </Badge>
        )}
        {!isScheduled && !isCompleted && !isCancelled && (
          <>
            {/* MED-845 AC11 (Kelley QA follow-up): Tooltip is suppressed when the coach
             *  has connected — preserves the pill's pre-MED-845 behavior in the happy path. */}
            <Tooltip
              label={AWAITING_COACH_TOOLTIP_COPY}
              withArrow
              position="top"
              multiline
              w={260}
              disabled={coachCalendlyConnected}
            >
              <Badge
                size="xs"
                color="warningAmber"
                variant="light"
                style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
              >
                Needs Scheduling
              </Badge>
            </Tooltip>
            {canSchedule && (
              <Button
                size="xs"
                variant="outline"
                color="dark"
                leftSection={<Calendar size={12} />}
                onClick={onSchedule}
              >
                Schedule
              </Button>
            )}
          </>
        )}
      </Group>
    </Group>
  );
}
